import { useCallback } from 'react';

export interface MemorySuggestion {
  content: string;
  type: 'profile' | 'event' | 'relation';
  confidence: number;
  context?: string;
  emotionTag?: string;
  topicTag?: string;
}

// 感情タグ抽出
function extractEmotionTag(text: string): string | undefined {
  if (/疲れ|しんどい|つらい|辛い|きつい|ヘトヘト|くたくた/.test(text)) return 'tired';
  if (/嬉しい|楽しい|最高|幸せ|良かった|うれしい|テンション/.test(text)) return 'happy';
  if (/悲しい|寂しい|泣|切ない|さみしい/.test(text)) return 'sad';
  if (/不安|心配|怖い|緊張|ドキドキ|びくびく/.test(text)) return 'anxious';
  if (/怒|イライラ|むかつく|腹立|ムカ/.test(text)) return 'angry';
  if (/驚|びっくり|まじで|信じられない/.test(text)) return 'surprised';
  return undefined;
}

// トピックタグ抽出
function extractTopicTag(text: string): string | undefined {
  const topics: Record<string, string[]> = {
    work: ['仕事', '会社', '上司', '残業', '職場', '同僚', '転職', '昇進', 'バイト', '社畜'],
    hobby: ['趣味', 'ゲーム', 'アニメ', '漫画', '映画', '音楽', '読書', 'スポーツ', '料理'],
    relationship: ['友達', '家族', '恋人', '彼氏', '彼女', '親', '兄弟', '姉妹', '先輩', '後輩'],
    health: ['体調', '疲れ', '眠い', '風邪', '病院', '睡眠', '食事', '運動'],
    event: ['誕生日', '記念日', '試験', '面接', '旅行', '引っ越し', '結婚', '卒業'],
    food: ['ご飯', '食べ', '料理', 'ランチ', '夕食', '朝食', 'カフェ', 'コーヒー'],
  };
  for (const [topic, keywords] of Object.entries(topics)) {
    if (keywords.some((kw) => text.includes(kw))) return topic;
  }
  return undefined;
}

export function useMemoryExtraction() {
  const extractMemories = useCallback((userMessage: string, aiResponse: string): MemorySuggestion[] => {
    const suggestions: MemorySuggestion[] = [];

    // ── イベント系 ──
    const eventPatterns = [
      { regex: /(仕事|会社|バイト|学校).*?(疲れ|大変|忙しい|残業|しんどい)/i, confidence: 0.82 },
      { regex: /(試験|テスト|面接|プレゼン|発表).*?(ある|受け|控え|終わ)/i, confidence: 0.92 },
      { regex: /(旅行|出張|帰省|旅).*?(行く|行った|予定|してきた)/i, confidence: 0.88 },
      { regex: /(風邪|体調|具合|熱).*?(悪い|崩し|引い|出た)/i, confidence: 0.9 },
      { regex: /(引っ越し|転職|異動|昇進|退職)/i, confidence: 0.95 },
      { regex: /(誕生日|記念日|イベント).*?(ある|近い|来週|今日|明日)/i, confidence: 0.88 },
      { regex: /(失恋|別れ|フラれ|振られ)/i, confidence: 0.93 },
      { regex: /(告白|付き合|デート).*?(した|する|予定)/i, confidence: 0.9 },
      { regex: /(試合|大会|コンテスト|発表会).*?(ある|出る|参加)/i, confidence: 0.87 },
      { regex: /(手術|入院|通院).*?(する|した|予定)/i, confidence: 0.95 },
    ];

    // ── 関係性系 ──
    const relationPatterns = [
      { regex: /(彼氏|彼女|恋人|パートナー).*?(いる|できた|別れ|喧嘩)/i, confidence: 0.92 },
      { regex: /(友達|友人|同僚|先輩|後輩).*?(喧嘩|相談|悩み|仲良|大切)/i, confidence: 0.82 },
      { regex: /(家族|親|兄弟|姉妹|母|父|お母|お父).*?(心配|連絡|会|喧嘩|仲)/i, confidence: 0.87 },
      { regex: /(上司|部下|先生|教授).*?(厳しい|優しい|怖い|苦手|好き)/i, confidence: 0.82 },
      { regex: /(好き|嫌い|苦手).*?(人|子|先輩|後輩|同僚)/i, confidence: 0.78 },
      { regex: /(一人暮らし|実家|同棲|同居)/i, confidence: 0.85 },
    ];

    // ── プロフィール系 ──
    const profilePatterns = [
      { regex: /(好き|嫌い|苦手).*?(食べ物|料理|音楽|映画|本|ゲーム|アニメ)/i, confidence: 0.72 },
      { regex: /(趣味|特技|得意|好きなこと).*?(は|が|って|：)/i, confidence: 0.78 },
      { regex: /(ペット|犬|猫|動物|うさぎ|ハムスター).*?(飼って|いる|いた)/i, confidence: 0.88 },
      { regex: /(職業|仕事|職種).*?(は|が|って|：)/i, confidence: 0.8 },
      { regex: /(住んで|暮らし|在住|出身).*?(いる|います|です)/i, confidence: 0.82 },
      { regex: /(血液型|星座|誕生日).*?(は|が|って|：)/i, confidence: 0.75 },
      { regex: /(好きな|お気に入り).*?(食べ物|曲|映画|本|場所|季節)/i, confidence: 0.72 },
    ];

    const allPatterns = [
      ...eventPatterns.map((p) => ({ ...p, type: 'event' as const })),
      ...relationPatterns.map((p) => ({ ...p, type: 'relation' as const })),
      ...profilePatterns.map((p) => ({ ...p, type: 'profile' as const })),
    ];

    allPatterns.forEach(({ regex, type, confidence }) => {
      const match = userMessage.match(regex);
      if (match) {
        const sentences = userMessage.split(/[。！？\n]/);
        const matchedSentence = sentences.find((s) => regex.test(s));
        if (matchedSentence && matchedSentence.trim().length > 5) {
          suggestions.push({
            content: matchedSentence.trim(),
            type,
            confidence,
            context: userMessage,
            emotionTag: extractEmotionTag(matchedSentence),
            topicTag: extractTopicTag(matchedSentence),
          });
        }
      }
    });

    // ── 感情表現を含む重要な発言 ──
    const emotionPatterns = [
      /(嬉しい|楽しい|幸せ|最高|テンション上がる)/i,
      /(悲しい|辛い|苦しい|寂しい|泣きそう)/i,
      /(不安|心配|怖い|緊張|ドキドキ)/i,
      /(イライラ|ムカつく|腹立つ|最悪)/i,
      /(びっくり|驚いた|信じられない|まじで)/i,
    ];

    emotionPatterns.forEach((regex) => {
      if (regex.test(userMessage)) {
        const sentences = userMessage.split(/[。！？\n]/);
        sentences.forEach((sentence) => {
          if (regex.test(sentence) && sentence.trim().length > 8) {
            const exists = suggestions.some((s) => s.content === sentence.trim());
            if (!exists) {
              suggestions.push({
                content: sentence.trim(),
                type: 'event',
                confidence: 0.75,
                context: userMessage,
                emotionTag: extractEmotionTag(sentence),
                topicTag: extractTopicTag(sentence),
              });
            }
          }
        });
      }
    });

    // ── 重複削除・信頼度ソート ──
    const unique = suggestions.filter(
      (s, i, arr) => arr.findIndex((t) => t.content === s.content) === i
    );
    return unique.sort((a, b) => b.confidence - a.confidence).slice(0, 3);
  }, []);

  const assessImportance = useCallback((suggestion: MemorySuggestion): number => {
    let importance = suggestion.confidence;
    if (suggestion.content.length < 10) importance -= 0.2;
    if (suggestion.content.length > 100) importance -= 0.1;
    if (suggestion.type === 'event') importance += 0.1;
    if (suggestion.type === 'relation') importance += 0.15;
    // 感情タグがある場合は重要度アップ
    if (suggestion.emotionTag) importance += 0.05;
    return Math.max(0, Math.min(1, importance));
  }, []);

  const shouldSaveAsMemory = useCallback(
    (suggestion: MemorySuggestion): boolean => {
      const importance = assessImportance(suggestion);
      return importance >= 0.6;
    },
    [assessImportance]
  );

  return { extractMemories, assessImportance, shouldSaveAsMemory };
}
